<script lang="ts">
  import { flattenDeep, groupBy } from 'lodash-es'
  import { useNavigate, useParams } from 'svelte-navigator'

  import { modalAnswerList, modalUserId, surveyStatus } from '../stores'
  import { QUESTIONS_DEF } from '../constants/questions_def'
  import { ROUTES } from '../constants/constants'
  import DataPortrait from './DataPortrait.svelte'
  import ShareButton from './ShareButton.svelte'
  import { calculateEmissions } from '../utils/emissions'

  const params = useParams()
  const isCurrentUserModal = $params.userId === $modalUserId

  const currentAnswerList = isCurrentUserModal ? $surveyStatus.userAnswersList : $modalAnswerList

  const navigate = useNavigate()

  const downloadImageURI = (uri, filename) => {
    const link = document.createElement('a')
    link.download = filename
    link.href = uri
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const answersWithQuestionText = flattenDeep(currentAnswerList)
    .filter(
      (answer) =>
        answer.questionId !== 'start_survey' &&
        answer.questionId !== 'end_of_survey' &&
        answer.questionId !== 'MID_VIZ' &&
        !answer.isOldAnswer
    )
    .map((answer) => {
      return {
        ...answer,
        questionText:
          // Retro-compatibility fix, yes ugly. Should be removable after going to prod
          answer.questionId === 'means_of_transport'
            ? QUESTIONS_DEF.find((q) => q?.id === 'used_means_of_transport_weekend').question
            : QUESTIONS_DEF.find((question) => question?.id === answer.questionId).question,
      }
    })

  const groupedAnswersWithQuestionText = Object.values(
    groupBy(answersWithQuestionText, 'questionId')
  )

  let innerWidth = window.innerWidth
  let stage
</script>

<svelte:window bind:innerWidth />

<div class="modal-header">
  <div class="portrait-wrapper">
    <DataPortrait
      bind:this={stage}
      width={innerWidth - 56 > 640 ? 640 : innerWidth - 56}
      answerList={currentAnswerList}
    />
  </div>

  <div class="emissions-recap">
    <div>
      <p>
        <span>{calculateEmissions(currentAnswerList)}</span>
        kgCO<sub>2</sub>e
      </p>
      <p>{isCurrentUserModal ? 'Le tue emissioni' : 'Emissioni'}</p>
    </div>

    {#if isCurrentUserModal}
      <div class="download-share">
        <svg
          class="download"
          on:click={() => {
            downloadImageURI(stage.createDataURL(), 'ritratto.png')
          }}
          width="34"
          height="34"
          viewBox="0 0 34 34"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="34" height="34" rx="17" fill="white" />
          <path d="M24 14H20V8H14V14H10L17 21L24 14ZM10 23V25H24V23H10Z" fill="black" />
        </svg>
        <ShareButton />
      </div>
    {/if}
  </div>
</div>

{#if isCurrentUserModal}
  <div class="links">
    <p>
      Scopri come essere più sostenibile. Ecco alcuni strumenti e portali utili per pianificare in
      maniera multimodale i tuoi spostamenti
    </p>
    <svg width="10" height="9" viewBox="0 0 10 9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.1 1C9.1 0.668629 8.83137 0.4 8.5 0.4L3.1 0.4C2.76863 0.4 2.5 0.668629 2.5 1C2.5 1.33137 2.76863 1.6 3.1 1.6L7.9 1.6V6.4C7.9 6.73137 8.16863 7 8.5 7C8.83137 7 9.1 6.73137 9.1 6.4V1ZM1.42426 8.92426L8.92426 1.42426L8.07574 0.575736L0.575736 8.07574L1.42426 8.92426Z"
        fill="#313131"
      />
    </svg>
    <a
      href={`/${ROUTES.usefulResources}`}
      target="_blank"
      on:click={(e) => {
        e.preventDefault()
        navigate(`/${ROUTES.usefulResources}`)
      }}
    >
      Migliora il tuo impatto
    </a>

    <br />

    <svg width="10" height="9" viewBox="0 0 10 9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.1 1C9.1 0.668629 8.83137 0.4 8.5 0.4L3.1 0.4C2.76863 0.4 2.5 0.668629 2.5 1C2.5 1.33137 2.76863 1.6 3.1 1.6L7.9 1.6V6.4C7.9 6.73137 8.16863 7 8.5 7C8.83137 7 9.1 6.73137 9.1 6.4V1ZM1.42426 8.92426L8.92426 1.42426L8.07574 0.575736L0.575736 8.07574L1.42426 8.92426Z"
        fill="#313131"
      />
    </svg>
    <a
      href={`/${ROUTES.bestPractices}`}
      target="_blank"
      on:click={(e) => {
        e.preventDefault()
        navigate(`/${ROUTES.bestPractices}`)
      }}
    >
      Buone pratiche
    </a>
    <br />
  </div>
{/if}

<div class="risposte">
  <h2>{isCurrentUserModal ? 'Le mie risposte' : 'Risposte'}</h2>
  {#each groupedAnswersWithQuestionText as answerGroup, index}
    <h3>{index + 1} - {answerGroup[0].questionText}</h3>
    {#each answerGroup as answer}
      {#if answerGroup.length > 1}
        <li>{answer.openAnswerText ? answer.openAnswerText : answer.text ? answer.text : '-'}</li>
      {:else}
        <p>{answer.openAnswerText ? answer.openAnswerText : answer.text ? answer.text : '-'}</p>
      {/if}
    {/each}
  {/each}
</div>

<style>
  .modal-header {
    overflow: hidden;
    background-color: #e4e4e4;
    border-radius: 0.5rem 0.5rem0 0;
  }

  .portrait-wrapper {
    min-height: 150px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .emissions-recap {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    font-weight: 600;
    padding: 1.5rem;
  }

  .emissions-recap svg {
    padding-left: 0.75rem;
    padding-top: 0.35rem;
  }
  .emissions-recap span {
    font-size: 34px;
  }
  .emissions-recap p {
    margin: 0;
  }

  .download-share {
    display: flex;
  }

  .download {
    cursor: pointer;
  }

  .links {
    font-size: 14px;
    padding: 1.5rem;
  }

  .links a {
    display: inline-block;
    text-decoration: none;
    color: #313131;
    font-size: 14px;
    font-weight: 600;
    border-bottom: 4px solid #00fff0;
    margin-bottom: 0.25rem;
  }

  .links svg {
    padding-right: 0.75rem;
  }

  .risposte {
    background-color: #000;
    color: white;
    font-weight: 600;
    font-size: 14px;
    padding: 1.5rem;
  }

  .risposte h3 {
    font-weight: normal;
    font-size: 14px;
    color: #7b7b7b;
    margin: 1rem 0 0;
  }

  .risposte p {
    margin: 0;
    font-size: 14px;
  }
  .risposte li {
    padding: 0.2rem 0;
    margin-left: 15px;
  }
</style>
